import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { ItemModel } from 'src/app/models/itemModel';
import type { LunaCreation } from 'src/app/models/luna-creation';
import type { AppIcon } from 'src/app/services/items-canvas.service';
import { splitStage, StageRect } from 'src/app/services/luna-workspace/luna-workspace-camera.util';
import {
  CreationSpot,
  CreationStatusLine,
  creatingTabsFor,
  creationSpots,
  creationStatusLines,
  newCreationTabIds,
} from 'src/app/services/luna-workspace/luna-creations.util';
import {
  AppPresentation,
  appPresentationFor,
  WHITEBOARD_TAB,
  WHITEBOARD_TAB_ID,
  isWorkspaceApp,
  isWorkspaceTabItem,
  workspaceAppKey,
  WorkspaceTab,
  workspaceTabLabel,
} from 'src/app/services/luna-workspace/luna-workspace-tabs.util';
// Type-only: importing the service module at runtime drags in the session services' circular
// imports, which unit tests cannot load.
import type { SessionView } from 'src/app/services/session-shared-data.service';

// `SessionView` values, spelled out so this file has no runtime import of the session services.
const WHITEBOARD_VIEW = 'WHITEBOARD' as SessionView;
const FULLSCREEN_APP_VIEW = 'FULLSCREEN_APP' as SessionView;

/** The slice of `SessionViewManager` workspace state needs. */
export interface WorkspaceSessionView {
  current$: Observable<{ view: SessionView; sessionViewMetaData?: { fullscreenId?: string } }>;
  switchToSessionView(
    view: SessionView,
    expandedFullScreenChange?: undefined,
    openTabs?: undefined,
    sessionViewMetaData?: { fullscreenId: string },
  ): unknown;
}

/** A board item as the tab bar sees it. */
export interface WorkspaceBoardItem {
  itemType: ItemModel;
  configurations: { uid: string };
  /** Board x. Apps inside the whiteboard column stay on the board instead of becoming tabs. */
  left?: number;
  /** The app's catalog icon, looked up by the caller. */
  icon?: AppIcon | null;
  /** The app's catalog title, looked up by the caller. */
  title?: string | null;
}

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

const sameRect = (a: StageRect | null, b: StageRect | null): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height &&
    a.fitWidth === b.fitWidth);

/**
 * Luna workspace mode's view state: the tabs (Whiteboard plus one per app on the board),
 * which tab is open, split view, and the stage rects the camera and apps size themselves to.
 * Opening an app tab reuses the existing full-screen app view; nothing here writes to the board.
 */
export class LunaWorkspaceState {
  private readonly appTabs = new BehaviorSubject<WorkspaceTab[]>([]);
  /** Apps inside the whiteboard column: no tab, but the student can still open one full screen. */
  private readonly boardApps = new BehaviorSubject<WorkspaceTab[]>([]);
  /** Visuals and apps Luna is making or has made (the `luna.creations` attribute). */
  private readonly creations = new BehaviorSubject<LunaCreation[]>([]);
  /** App tabs the student has opened, so a new one stops being marked new. */
  private readonly seenTabIds = new BehaviorSubject<ReadonlySet<string>>(new Set());
  private readonly split = new BehaviorSubject<string | null>(null);
  private readonly whiteboardStage = new BehaviorSubject<StageRect | null>(null);
  protected readonly subscriptions = new Subscription();
  private fullscreenIdNow: string | null = null;
  private enabledNow = false;

  private readonly fullscreenId$: Observable<string | null>;

  /**
   * Whiteboard, the app tabs, a whiteboard app while it is open full screen (so the bar shows
   * where the student is), then a tab for each app Luna is still making.
   */
  readonly tabs$: Observable<WorkspaceTab[]>;

  /** App tabs Luna made that the student hasn't opened yet (they get a "new" dot). */
  readonly newTabIds$: Observable<string[]> = combineLatest([
    this.appTabs,
    this.creations,
    this.seenTabIds,
  ]).pipe(
    map(([apps, creations, seen]) =>
      newCreationTabIds(
        creations,
        apps.map((tab) => tab.id),
        seen,
      ),
    ),
    distinctUntilChanged(sameIds),
  );

  /** The Luna panel's one-line status for each thing Luna is making (see creationStatusLines). */
  readonly creationLines$: Observable<CreationStatusLine[]>;

  /** Whiteboard spots held by visuals and apps Luna is still making (see creationSpots). */
  readonly creationSpots$: Observable<CreationSpot[]> = this.creations.pipe(map(creationSpots));

  readonly splitItemUid$: Observable<string | null> = this.split.pipe(distinctUntilChanged());

  readonly activeTabIds$: Observable<string[]>;

  /** An app is on screen, full screen or beside the whiteboard. */
  readonly appVisible$: Observable<boolean>;

  readonly whiteboardStage$: Observable<StageRect | null> = this.whiteboardStage.pipe(
    distinctUntilChanged(sameRect),
  );

  /** Where the whiteboard column is drawn: the whole stage, or its left half in split view. */
  readonly boardStage$: Observable<StageRect | null> = combineLatest([
    this.whiteboardStage$,
    this.splitItemUid$,
  ]).pipe(
    map(([stage, splitId]) => (stage && splitId ? splitStage(stage).board : stage)),
    distinctUntilChanged(sameRect),
  );

  /** Where the split app is drawn, or null outside split view. */
  readonly splitAppRect$: Observable<StageRect | null> = combineLatest([
    this.whiteboardStage$,
    this.splitItemUid$,
  ]).pipe(
    map(([stage, splitId]) => (stage && splitId ? splitStage(stage).app : null)),
    distinctUntilChanged(sameRect),
  );

  constructor(
    readonly enabled$: Observable<boolean>,
    private readonly sessionView: WorkspaceSessionView,
  ) {
    this.fullscreenId$ = sessionView.current$.pipe(
      map((current) =>
        current.view === FULLSCREEN_APP_VIEW
          ? current.sessionViewMetaData?.fullscreenId ?? null
          : null,
      ),
      distinctUntilChanged(),
    );

    this.tabs$ = combineLatest([
      this.appTabs,
      this.boardApps,
      this.fullscreenId$,
      this.creations,
    ]).pipe(
      map(([apps, boardApps, fullscreenId, creations]) => [
        WHITEBOARD_TAB,
        ...apps,
        ...boardApps.filter((tab) => tab.id === fullscreenId),
        ...creatingTabsFor(
          creations,
          apps.map((tab) => tab.id),
        ),
      ]),
    );

    this.creationLines$ = combineLatest([this.tabs$, this.creations, this.seenTabIds]).pipe(
      map(([tabs, creations, seen]) => creationStatusLines(creations, tabs, seen)),
    );

    this.activeTabIds$ = combineLatest([this.fullscreenId$, this.splitItemUid$]).pipe(
      map(([fullscreenId, splitId]) => {
        if (fullscreenId) {
          return [fullscreenId];
        }
        return splitId ? [WHITEBOARD_TAB_ID, splitId] : [WHITEBOARD_TAB_ID];
      }),
      distinctUntilChanged(sameIds),
    );

    // Only in workspace mode: outside it a full-screen app must not change the rest of the UI.
    this.appVisible$ = combineLatest([enabled$, this.fullscreenId$, this.splitItemUid$]).pipe(
      map(([enabled, fullscreenId, splitId]) => enabled && (!!fullscreenId || !!splitId)),
      distinctUntilChanged(),
    );

    this.subscriptions.add(enabled$.subscribe((enabled) => (this.enabledNow = enabled)));
    this.subscriptions.add(
      this.fullscreenId$.subscribe((fullscreenId) => {
        this.fullscreenIdNow = fullscreenId;
        if (fullscreenId) {
          this.markSeen(fullscreenId);
        }
        // Full screen replaces split, e.g. from the split app's own full-screen button.
        if (fullscreenId) {
          this.split.next(null);
        }
      }),
    );
  }

  setWhiteboardStage(rect: StageRect): void {
    this.whiteboardStage.next(rect);
  }

  getBoardStage(): StageRect | null {
    const stage = this.whiteboardStage.getValue();
    return stage && this.split.getValue() ? splitStage(stage).board : stage;
  }

  /** Rebuilds the app tabs from the board's items, and closes split if its app is gone. */
  setBoardItems(items: WorkspaceBoardItem[]): void {
    const toTab = (item: WorkspaceBoardItem): WorkspaceTab => ({
      kind: 'app',
      id: item.configurations.uid,
      label: workspaceTabLabel(item.itemType, item.configurations, item.title),
      itemType: item.itemType,
      appKey: workspaceAppKey(item.itemType, item.configurations),
      icon: item.icon ?? null,
    });
    const apps = items.filter((item) => isWorkspaceApp(item.itemType));
    const tabs = apps.filter((item) => isWorkspaceTabItem(item.itemType, item.left)).map(toTab);
    this.appTabs.next(tabs);
    this.boardApps.next(
      apps.filter((item) => !isWorkspaceTabItem(item.itemType, item.left)).map(toTab),
    );

    const splitId = this.split.getValue();
    if (splitId && !tabs.some((tab) => tab.id === splitId)) {
      this.split.next(null);
    }
    // The app open full screen was removed (e.g. Luna deleted it): back to the whiteboard,
    // rather than a full-screen view of nothing with the whiteboard's toolbar hidden. A
    // whiteboard app open full screen is still an app on the board, not a removed one.
    const fullscreenId = this.fullscreenIdNow;
    if (
      this.enabledNow &&
      fullscreenId &&
      !apps.some((item) => item.configurations.uid === fullscreenId)
    ) {
      this.sessionView.switchToSessionView(WHITEBOARD_VIEW);
    }
  }

  /** The latest `luna.creations` list from the agent. */
  setCreations(creations: LunaCreation[]): void {
    this.creations.next(creations);
  }

  private markSeen(tabId: string): void {
    const seen = this.seenTabIds.getValue();
    if (!seen.has(tabId)) {
      this.seenTabIds.next(new Set([...seen, tabId]));
    }
  }

  openWhiteboard(): void {
    // In split view the whiteboard is already on screen.
    if (this.split.getValue()) {
      return;
    }
    this.sessionView.switchToSessionView(WHITEBOARD_VIEW);
  }

  openApp(itemUid: string): void {
    this.markSeen(itemUid);
    if (this.split.getValue()) {
      this.split.next(itemUid);
      return;
    }
    this.sessionView.switchToSessionView(FULLSCREEN_APP_VIEW, undefined, undefined, {
      fullscreenId: itemUid,
    });
  }

  /** Opens a tab as the tab bar does: the whiteboard, or the app full screen (or into split). */
  openTab(tab: WorkspaceTab): void {
    if (tab.kind === 'whiteboard') {
      this.openWhiteboard();
    } else if (tab.kind === 'creating') {
      // Still being made: the tab bar shows its progress instead.
      return;
    } else {
      this.openApp(tab.id);
    }
  }

  /** Opens the full-screen app (or the first app) beside the whiteboard, or closes split view. */
  toggleSplit(): void {
    if (this.split.getValue()) {
      this.closeSplit();
      return;
    }
    const fullscreenId = this.fullscreenIdNow;
    const tabs = this.appTabs.getValue();
    // Only an app tab goes beside the whiteboard: a whiteboard app is already on it.
    const fullscreenTab = tabs.some((tab) => tab.id === fullscreenId) ? fullscreenId : null;
    const target = fullscreenTab ?? tabs[0]?.id ?? null;
    if (!target) {
      return;
    }
    if (fullscreenId) {
      this.sessionView.switchToSessionView(WHITEBOARD_VIEW);
    }
    this.split.next(target);
  }

  closeSplit(): void {
    this.split.next(null);
  }

  appPresentation$(itemUid: string): Observable<AppPresentation> {
    const isTab$ = this.appTabs.pipe(map((tabs) => tabs.some((tab) => tab.id === itemUid)));
    return combineLatest([this.enabled$, this.fullscreenId$, this.splitItemUid$, isTab$]).pipe(
      map(([enabled, fullscreenId, splitItemUid, isTab]) =>
        appPresentationFor({ enabled, fullscreenId, splitItemUid }, itemUid, isTab),
      ),
      distinctUntilChanged(),
    );
  }

  /** Forgets the board's apps and split view, e.g. when the canvas goes away. */
  clearBoard(): void {
    this.appTabs.next([]);
    this.boardApps.next([]);
    this.split.next(null);
  }

  dispose(): void {
    this.subscriptions.unsubscribe();
  }
}
